import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { get } from 'api/utils';
import ENDPOINTS from 'api/utils/endpoints';
import useQueryKeys from 'api/utils/useQueryKeys';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { userAtom } from 'store';
import { User } from 'types/User';
import { assertIsDefined } from 'util/assert';

interface GetStatusRequest {
  user?: User;
  dealerId?: number;
}

export interface GetStatusResponse {
  dealerId: number;
  lastRuntime: number;
  lastUpdated: string;
  status: 'COMPLETE' | 'RUNNING' | 'ERROR' | 'PENDING' | 'UNKNOWN';
}

async function fetchCacheStatus({ user, dealerId }: GetStatusRequest) {
  assertIsDefined(user?.token);
  // assertIsDefined(dealerId);

  if (
    !user ||
    user?.accountType !== 'OWNER' ||
    !(user.email.indexOf('@lotlinx.com') > -1) ||
    dealerId === undefined
  ) {
    return {
      dealerId: dealerId,
      lastRuntime: 0,
      lastUpdated: '',
      status: 'COMPLETE',
    };
  }

  return await get<GetStatusResponse>(`${ENDPOINTS.getCacheStatus}/${dealerId}`, {
    headers: { token: user?.token },
  });
}

export function useGetInitialCacheStatus() {
  const [user] = useAtom(userAtom);
  const [dealerId] = useSelectedDealerId();
  const { initialCacheStatusKey } = useQueryKeys();

  return useQuery({
    queryKey: initialCacheStatusKey,
    refetchOnMount: false,
    queryFn: () =>
      fetchCacheStatus({
        user,
        dealerId,
      }),
  });
}

export default function useGetCacheStatus(cacheDealerList: number[]) {
  const [user] = useAtom(userAtom);
  const { cacheStatusKey } = useQueryKeys();

  return useQueries({
    // cacheStatusKey carries no dealerId of its own (see useQueryKeys) — each row needs its own
    // key, one per dealer in the list, which the hook can't produce on its own since it's only
    // callable once per component.
    queries: cacheDealerList.map((dealerId) => ({
      queryKey: [...cacheStatusKey, dealerId],
      queryFn: () =>
        fetchCacheStatus({
          user,
          dealerId,
        }),
      refetchOnMount: true,
    })),
  });
}
